import React, { createContext, useContext, useState, useEffect } from 'react';

const dict = {
  en: {
    'nav.methodology': 'Methodology',
    'nav.products': 'Products',
    'nav.samples': 'Sample',
    'nav.pilot': 'Beta Pilot',
    'nav.intake': 'Request Report',
    'nav.admin': 'Admin',
    'nav.contact': 'Contact',
    'hero.eyebrow': 'REAL ESTATE DECISION INTELLIGENCE',
    'hero.title.a': 'The Oracle,',
    'hero.title.b': 'Modernized.',
    'hero.subtitle': 'Pythia converts fragmented real estate data into decision-grade evidence: AVM ranges, risk stack, source ledger, missingness, and a verdict you can defend before a check clears.',
    'hero.cta.primary': 'Request a Deal Report',
    'hero.cta.secondary': 'See a Sample',
    'hero.disclaimer': 'Not an appraisal. Not investment advice. Model-assisted due diligence.',
    'ticker.title': 'LIVE SIGNALS',
    'problem.title': 'The problem is not data. It is unfalsifiable belief.',
    'problem.sub': 'Brokers push narratives. AVMs pretend to be truth. CMAs stop at surface comps. Investors pay the difference.',
    'problem.a.title': 'Fragmented sources',
    'problem.a.body': 'Public records, MLS packets, permits, flood maps, absentee data — none of them talk to each other.',
    'problem.b.title': 'Confident hallucinations',
    'problem.b.body': 'Off-the-shelf AI writes a beautiful PDF that cannot cite a single source, and cannot say "I don\'t know."',
    'problem.c.title': 'Silent missingness',
    'problem.c.body': 'The most dangerous data point is the one that isn\'t there. Standard tools hide it. Pythia declares it.',
    'method.eyebrow': 'THE PYTHIA-BOMBE METHOD',
    'method.title': 'Hypothesis. Falsification. Contradiction. Verdict.',
    'method.body': 'Every report forms hypotheses about the deal, actively seeks evidence that would falsify them, exposes contradictions between sources, and quantifies what remains unknown. This is the difference between a decision and a guess.',
    'products.title': 'Three lines of intelligence.',
    'products.sub': 'Priced for how you buy — a single verdict, a monthly territory, or a full desk.',
    'product.a.badge': 'WEDGE',
    'product.a.title': 'Deal Decision Report',
    'product.a.price': '$100',
    'product.a.unit': 'per property · 48h delivery',
    'product.a.list': [
      'Deal verdict + confidence',
      'AVM/CMA evidence range',
      'Risk stack (flood, permits, insurance)',
      'Source ledger with missingness',
      'Offer checklist + red flags',
      'Negotiation levers',
    ],
    'product.a.cta': 'Order a Report',
    'product.b.badge': 'RETAINER',
    'product.b.title': 'Territory Radar',
    'product.b.price': '$750',
    'product.b.unit': 'per month · ZIP-level',
    'product.b.list': [
      'Monthly shortlist by territory',
      'Absentee / stale / permits signals',
      'Distress + policy context',
      'Deal alerts before MLS',
      '2 deep dives included',
    ],
    'product.b.cta': 'Join Waitlist',
    'product.c.badge': 'PREMIUM',
    'product.c.title': 'Investor Intelligence Desk',
    'product.c.price': '$1,000',
    'product.c.unit': 'per month · concierge',
    'product.c.list': [
      'Up to 20 properties/month',
      'Deal memos + pipeline review',
      'Territory radar included',
      'Strategy call every two weeks',
      'White-glove intake',
    ],
    'product.c.cta': 'Talk to Us',
    'radar.title': 'Territory Radar — live preview',
    'radar.sub': 'Signal scores across South Florida ZIPs. Full ownership + opportunity counts unlocked with retainer.',
    'radar.col.zip': 'ZIP',
    'radar.col.name': 'Territory',
    'radar.col.signal': 'Signal',
    'radar.col.dom': 'Median DOM',
    'radar.col.change': 'Δ Price',
    'radar.col.flood': 'Flood',
    'radar.col.owners': 'Absentee',
    'radar.col.opps': 'Opps',
    'sample.title': 'Anatomy of a Pythia report',
    'sample.sub': 'Every claim carries a source, a confidence, and a missingness flag. If we cannot know, we say so.',
    'compliance.title': 'What Pythia is — and is not.',
    'compliance.is': 'Pythia IS',
    'compliance.isnot': 'Pythia is NOT',
    'compliance.is.a': 'Model-assisted due diligence',
    'compliance.is.b': 'Decision intelligence infrastructure',
    'compliance.is.c': 'A source ledger + missingness engine',
    'compliance.is.d': 'A negotiation and verification framework',
    'compliance.isnot.a': 'An appraisal (USPAP)',
    'compliance.isnot.b': 'A lender-grade AVM',
    'compliance.isnot.c': 'Investment advice',
    'compliance.isnot.d': 'A guarantee of value or return',
    'pilot.title': 'The Beta Pilot',
    'pilot.body': 'We are accepting 10 pilot properties this week at $100. Standard price is $100. Investor memo rush is $799. This is the fastest way to test whether a Pythia report changes your decision.',
    'pilot.cta': 'Claim a Pilot Slot',
    'cta.final.title': 'Better decisions. Fewer bad checks.',
    'cta.final.body': 'One property. Forty-eight hours. A verdict you can defend.',
    'cta.final.button': 'Start Your First Report',
    'footer.tagline': 'Not an appraisal. Not investment advice. Decision Intelligence.',
    'footer.rights': 'All rights reserved.',
    // Intake
    'intake.title': 'Request a Deal Decision Report',
    'intake.sub': 'Give us the property. We will give you back a verdict, a range, a risk stack, and a source ledger — in 48 hours.',
    'intake.section.contact': 'Contact',
    'intake.section.property': 'Property',
    'intake.section.intent': 'Your Intent',
    'intake.section.product': 'Product',
    'intake.field.name': 'Full name',
    'intake.field.email': 'Email',
    'intake.field.phone': 'Phone (optional)',
    'intake.field.address': 'Property address',
    'intake.field.folio': 'Folio / Parcel ID (optional)',
    'intake.field.asking': 'Asking price (USD)',
    'intake.field.use': 'Intended use',
    'intake.use.buy_hold': 'Buy & hold',
    'intake.use.fix_flip': 'Fix & flip',
    'intake.use.primary': 'Primary residence',
    'intake.use.wholesale': 'Wholesale',
    'intake.use.listing_autopsy': 'Listing autopsy (broker/seller)',
    'intake.use.other': 'Other',
    'intake.field.profile': 'Buyer profile (cash, financed, LLC, etc.)',
    'intake.field.timeline': 'Timeline',
    'intake.field.risks': 'Known risks / concerns',
    'intake.field.target': 'What decision are you trying to make?',
    'intake.field.mls': 'I authorize Pythia to consult any MLS packet I share.',
    'intake.field.product': 'Product',
    'intake.product.deal_decision_report': 'Deal Decision Report — $100 (48h)',
    'intake.product.rush': 'Rush Report — $799 (24h)',
    'intake.product.premium_memo': 'Premium Investor Memo — $799',
    'intake.submit': 'Submit Request',
    'intake.submit.pay': 'Continue to Payment',
    'intake.pay.title': 'Pay now with card',
    'intake.pay.body': 'Securely pay with Stripe now to lock your slot. Uncheck if you prefer to pay after we confirm the intake.',
    'intake.success': 'Received. We will confirm within 4 business hours.',
    // Methodology
    'meth.title': 'The Pythia-BOMBE method',
    'meth.sub': 'How we turn fragmented data into a defensible verdict.',
    'meth.s1.title': '1 · Hypothesis',
    'meth.s1.body': 'We frame every deal as a set of testable hypotheses about value, risk, and intent. Never a single number.',
    'meth.s2.title': '2 · Evidence',
    'meth.s2.body': 'Each hypothesis is grounded in a source. Every source carries a confidence score and a missingness flag.',
    'meth.s3.title': '3 · Falsification',
    'meth.s3.body': 'We actively seek evidence that would kill the deal. If we cannot find any, that is itself information.',
    'meth.s4.title': '4 · Contradiction',
    'meth.s4.body': 'Where sources disagree, we surface the conflict instead of averaging it away.',
    'meth.s5.title': '5 · Missingness',
    'meth.s5.body': 'What we do not know is declared, not hidden. Silent gaps are the most expensive data points.',
    'meth.s6.title': '6 · Verdict',
    'meth.s6.body': 'Proceed · Negotiate · Hold · Reject — with confidence, checklist, and the verification questions that remain.',
    // Admin
    'admin.login.title': 'Admin Console',
    'admin.login.sub': 'Enter your Pythia admin token.',
    'admin.login.button': 'Unlock',
    'admin.login.error': 'Invalid token.',
    'admin.nav.overview': 'Overview',
    'admin.nav.intakes': 'Intakes',
    'admin.nav.reports': 'Reports',
    'admin.nav.radar': 'Territory Radar',
    'admin.nav.pilot': 'Pilot Tracker',
    'admin.nav.generate': 'Generate Report',
    'admin.nav.leads': 'Leads',
    'admin.logout': 'Lock',
    // Verdicts
    'verdict.proceed': 'Proceed',
    'verdict.negotiate': 'Negotiate',
    'verdict.hold': 'Hold',
    'verdict.reject': 'Reject',
  },
  es: {
    'nav.methodology': 'Metodología',
    'nav.products': 'Productos',
    'nav.samples': 'Muestra',
    'nav.pilot': 'Piloto Beta',
    'nav.intake': 'Solicitar Reporte',
    'nav.admin': 'Admin',
    'nav.contact': 'Contacto',
    'hero.eyebrow': 'INTELIGENCIA DE DECISIÓN INMOBILIARIA',
    'hero.title.a': 'El Oráculo,',
    'hero.title.b': 'modernizado.',
    'hero.subtitle': 'Pythia convierte datos inmobiliarios fragmentados en evidencia decisional: rangos AVM, stack de riesgos, ledger de fuentes, missingness y un veredicto defendible antes de firmar cheque.',
    'hero.cta.primary': 'Solicitar Reporte',
    'hero.cta.secondary': 'Ver Muestra',
    'hero.disclaimer': 'No es una tasación. No es asesoría de inversión. Due diligence asistido por modelo.',
    'ticker.title': 'SEÑALES EN VIVO',
    'problem.title': 'El problema no son los datos. Es la creencia infalsificable.',
    'problem.sub': 'Los brokers empujan narrativas. Los AVMs fingen ser verdad. Los CMAs se quedan en la superficie. El inversor paga la diferencia.',
    'problem.a.title': 'Fuentes fragmentadas',
    'problem.a.body': 'Registros públicos, MLS, permisos, mapas de inundación, dueños ausentes — ninguno habla con el otro.',
    'problem.b.title': 'Alucinaciones seguras',
    'problem.b.body': 'La IA genérica escribe un PDF bonito que no cita fuentes y no sabe decir "no sé".',
    'problem.c.title': 'Missingness silencioso',
    'problem.c.body': 'El dato más peligroso es el que no está. Las herramientas estándar lo ocultan. Pythia lo declara.',
    'method.eyebrow': 'MÉTODO PYTHIA-BOMBE',
    'method.title': 'Hipótesis. Falsación. Contradicción. Veredicto.',
    'method.body': 'Cada reporte formula hipótesis sobre el deal, busca activamente evidencia que las falsee, expone contradicciones entre fuentes, y cuantifica lo que sigue siendo desconocido. Esa es la diferencia entre una decisión y una corazonada.',
    'products.title': 'Tres líneas de inteligencia.',
    'products.sub': 'Precios según cómo compras — un veredicto puntual, un territorio mensual, o un desk completo.',
    'product.a.badge': 'ENTRADA',
    'product.a.title': 'Deal Decision Report',
    'product.a.price': '$100',
    'product.a.unit': 'por propiedad · entrega 48h',
    'product.a.list': [
      'Veredicto + confianza',
      'Rango AVM/CMA',
      'Stack de riesgos (inundación, permisos, seguro)',
      'Ledger de fuentes + missingness',
      'Checklist de oferta + red flags',
      'Palancas de negociación',
    ],
    'product.a.cta': 'Solicitar Reporte',
    'product.b.badge': 'RETAINER',
    'product.b.title': 'Territory Radar',
    'product.b.price': '$750',
    'product.b.unit': 'por mes · a nivel ZIP',
    'product.b.list': [
      'Shortlist mensual por territorio',
      'Señales absentee / stale / permits',
      'Distress + contexto de política',
      'Alertas antes de MLS',
      '2 deep dives incluidos',
    ],
    'product.b.cta': 'Unirme a la Lista',
    'product.c.badge': 'PREMIUM',
    'product.c.title': 'Investor Intelligence Desk',
    'product.c.price': '$1,000',
    'product.c.unit': 'por mes · concierge',
    'product.c.list': [
      'Hasta 20 propiedades/mes',
      'Deal memos + revisión de pipeline',
      'Territory radar incluido',
      'Llamada estratégica quincenal',
      'Intake asistido',
    ],
    'product.c.cta': 'Hablar con Pythia',
    'radar.title': 'Territory Radar — vista previa',
    'radar.sub': 'Puntajes de señal por ZIP en el sur de Florida. Propiedad + oportunidades se desbloquean con retainer.',
    'radar.col.zip': 'ZIP',
    'radar.col.name': 'Territorio',
    'radar.col.signal': 'Señal',
    'radar.col.dom': 'DOM Mediano',
    'radar.col.change': 'Δ Precio',
    'radar.col.flood': 'Inund.',
    'radar.col.owners': 'Absentee',
    'radar.col.opps': 'Opps',
    'sample.title': 'Anatomía de un reporte Pythia',
    'sample.sub': 'Cada afirmación lleva fuente, confianza y bandera de missingness. Si no podemos saberlo, lo decimos.',
    'compliance.title': 'Lo que Pythia es — y no es.',
    'compliance.is': 'Pythia SÍ es',
    'compliance.isnot': 'Pythia NO es',
    'compliance.is.a': 'Due diligence asistido por modelo',
    'compliance.is.b': 'Infraestructura de inteligencia de decisión',
    'compliance.is.c': 'Un ledger de fuentes + motor de missingness',
    'compliance.is.d': 'Un framework de negociación y verificación',
    'compliance.isnot.a': 'Una tasación (USPAP)',
    'compliance.isnot.b': 'Un AVM lender-grade',
    'compliance.isnot.c': 'Asesoría de inversión',
    'compliance.isnot.d': 'Una garantía de valor o retorno',
    'pilot.title': 'El Piloto Beta',
    'pilot.body': 'Aceptamos 10 propiedades piloto esta semana a $100. Precio normal $100. Investor memo rush $799. Es la forma más rápida de probar si un reporte Pythia cambia tu decisión.',
    'pilot.cta': 'Reservar Cupo Beta',
    'cta.final.title': 'Mejores decisiones. Menos cheques malos.',
    'cta.final.body': 'Una propiedad. Cuarenta y ocho horas. Un veredicto defendible.',
    'cta.final.button': 'Iniciar Mi Primer Reporte',
    'footer.tagline': 'No es tasación. No es asesoría de inversión. Inteligencia de decisión.',
    'footer.rights': 'Todos los derechos reservados.',
    'intake.title': 'Solicitar Deal Decision Report',
    'intake.sub': 'Danos la propiedad. Te devolvemos veredicto, rango, stack de riesgos y ledger de fuentes en 48 horas.',
    'intake.section.contact': 'Contacto',
    'intake.section.property': 'Propiedad',
    'intake.section.intent': 'Intención',
    'intake.section.product': 'Producto',
    'intake.field.name': 'Nombre completo',
    'intake.field.email': 'Email',
    'intake.field.phone': 'Teléfono (opcional)',
    'intake.field.address': 'Dirección de propiedad',
    'intake.field.folio': 'Folio / Parcel ID (opcional)',
    'intake.field.asking': 'Precio pedido (USD)',
    'intake.field.use': 'Uso previsto',
    'intake.use.buy_hold': 'Compra & renta',
    'intake.use.fix_flip': 'Fix & flip',
    'intake.use.primary': 'Residencia principal',
    'intake.use.wholesale': 'Wholesale',
    'intake.use.listing_autopsy': 'Autopsia de listing (broker/vendedor)',
    'intake.use.other': 'Otro',
    'intake.field.profile': 'Perfil comprador (cash, financiado, LLC, etc.)',
    'intake.field.timeline': 'Timeline',
    'intake.field.risks': 'Riesgos conocidos / preocupaciones',
    'intake.field.target': '¿Qué decisión intentas tomar?',
    'intake.field.mls': 'Autorizo a Pythia a revisar cualquier packet MLS que comparta.',
    'intake.field.product': 'Producto',
    'intake.product.deal_decision_report': 'Deal Decision Report — $100 (48h)',
    'intake.product.rush': 'Rush Report — $799 (24h)',
    'intake.product.premium_memo': 'Premium Investor Memo — $799',
    'intake.submit': 'Enviar Solicitud',
    'intake.submit.pay': 'Continuar al Pago',
    'intake.pay.title': 'Pagar ahora con tarjeta',
    'intake.pay.body': 'Paga con Stripe ahora para reservar tu cupo. Desmarca si prefieres pagar después de confirmar el intake.',
    'intake.success': 'Recibido. Confirmamos en 4 horas hábiles.',
    'meth.title': 'El método Pythia-BOMBE',
    'meth.sub': 'Cómo convertimos datos fragmentados en un veredicto defendible.',
    'meth.s1.title': '1 · Hipótesis',
    'meth.s1.body': 'Formulamos cada deal como un conjunto de hipótesis falseables sobre valor, riesgo e intención. Nunca un solo número.',
    'meth.s2.title': '2 · Evidencia',
    'meth.s2.body': 'Cada hipótesis se sostiene en una fuente. Cada fuente lleva confianza y bandera de missingness.',
    'meth.s3.title': '3 · Falsación',
    'meth.s3.body': 'Buscamos activamente evidencia que mate el deal. Si no la hallamos, eso también es información.',
    'meth.s4.title': '4 · Contradicción',
    'meth.s4.body': 'Cuando las fuentes se contradicen, lo mostramos en vez de promediarlo.',
    'meth.s5.title': '5 · Missingness',
    'meth.s5.body': 'Lo desconocido se declara, no se oculta. Los vacíos silenciosos son los datos más caros.',
    'meth.s6.title': '6 · Veredicto',
    'meth.s6.body': 'Proceder · Negociar · Sostener · Rechazar — con confianza, checklist y las preguntas que quedan por verificar.',
    'admin.login.title': 'Consola Admin',
    'admin.login.sub': 'Ingresa tu token de admin de Pythia.',
    'admin.login.button': 'Desbloquear',
    'admin.login.error': 'Token inválido.',
    'admin.nav.overview': 'Overview',
    'admin.nav.intakes': 'Intakes',
    'admin.nav.reports': 'Reportes',
    'admin.nav.radar': 'Territory Radar',
    'admin.nav.pilot': 'Pilot Tracker',
    'admin.nav.generate': 'Generar Reporte',
    'admin.nav.leads': 'Leads',
    'admin.logout': 'Bloquear',
    'verdict.proceed': 'Proceder',
    'verdict.negotiate': 'Negociar',
    'verdict.hold': 'Sostener',
    'verdict.reject': 'Rechazar',
  },
};

const I18nContext = createContext({ lang: 'en', setLang: () => {}, t: (k) => k });

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem('pythia_lang') || 'en');
  useEffect(() => { localStorage.setItem('pythia_lang', lang); }, [lang]);

  const t = (key) => {
    const v = dict[lang]?.[key];
    if (v === undefined) return key;
    return v;
  };

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() { return useContext(I18nContext); }
